import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { useDeleteTag } from '@utils/api/organizations/tags';
import { Tag } from '@utils/types/organization/tag.ts';

interface DeleteTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag: Tag | null;
  onDeleteSuccess?: () => void;
}

export default function DeleteTagModal({
  isOpen,
  onClose,
  tag,
  onDeleteSuccess
}: DeleteTagModalProps) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteTag();

  const handleConfirm = async () => {
    if (!tag) return;

    try {
      await deleteMutation.mutateAsync(tag.id);
      toast.success(t('tag-deleted-successfully') || 'Tag deleted');
      onDeleteSuccess?.();
      onClose();
    } catch {
      toast.error(t('failed-to-delete-tag') || 'Failed to delete tag');
    }
  };

  if (!tag) return null;

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title={t('delete-tag') || 'Delete tag'}
      caption={
        t('confirm-delete-tag', { name: tag.name }) ||
        `Delete tag "${tag.name}"? This also removes all selections marked with it.`
      }
      confirmLabel={t('delete')}
      cancelLabel={t('cancel')}
      destructive
      confirmDisabled={deleteMutation.isLoading}
    />
  );
}
